import React, { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useReveal } from "../hooks/useReveal";
import { LightStreaks } from "./LightStreaks";

export const LeadForm = () => {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error("Wypełnij wszystkie pola");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm({ name: "", email: "", description: "" });
      toast.success("Zgłoszenie wysłane. Odezwiemy się szybko.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Coś poszło nie tak. Spróbuj ponownie."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      ref={ref}
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden"
    >
      <LightStreaks density={5} />

      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5 reveal">
          <span className="text-xs tracking-[0.2em] uppercase text-stormly-cyan font-bold">
            Kontakt
          </span>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.02]">
            Gotowy na efekt{" "}
            <span className="text-stormly-cyan">po burzy?</span>
          </h2>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            Napisz krótko, czego potrzebujesz. Odezwiemy się w ciągu kilku
            godzin z pierwszą propozycją i wyceną.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="tel:+48507890059"
              data-testid="contact-phone"
              className="flex items-center gap-4 text-white hover:text-stormly-cyan transition-colors group"
            >
              <span className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-stormly-cyan/40 transition-colors">
                <Phone size={16} />
              </span>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-widest">
                  Telefon
                </p>
                <p className="font-semibold">+48 507 890 059</p>
              </div>
            </a>
            <a
              href="mailto:stormly.info@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-4 text-white hover:text-stormly-cyan transition-colors group"
            >
              <span className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-stormly-cyan/40 transition-colors">
                <Mail size={16} />
              </span>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-widest">
                  E-mail
                </p>
                <p className="font-semibold">stormly.info@gmail.com</p>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 reveal reveal-delay-2">
          <div className="relative bg-[#1F1F38]/80 backdrop-blur-md border border-white/10 rounded-3xl p-7 md:p-10">
            <div
              className="absolute -top-24 -right-24 h-64 w-64 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,255,255,0.15), transparent 70%)",
              }}
            />

            {done ? (
              <div
                data-testid="form-success"
                className="text-center py-8"
              >
                <div className="mx-auto h-16 w-16 rounded-full bg-stormly-cyan/15 border border-stormly-cyan/40 flex items-center justify-center mb-5">
                  <CheckCircle2 className="text-stormly-cyan" size={28} />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">
                  Mamy Twoje zgłoszenie.
                </h3>
                <p className="text-white/65 mb-6">
                  Odezwiemy się jak najszybciej — zazwyczaj w ciągu 1–3 godzin
                  w godzinach pracy.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="btn-ghost"
                  data-testid="form-send-another"
                >
                  Wyślij kolejne
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="space-y-5 relative"
                data-testid="lead-form"
              >
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">
                    Imię
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Jak się nazywasz?"
                    className="form-input"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">
                    E-mail
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="twoj@email.pl"
                    className="form-input"
                    data-testid="form-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">
                    Opis projektu
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    placeholder="Krótko: o czym ma być strona, do kiedy, czy masz przykłady które się podobają..."
                    rows={5}
                    className="form-input resize-none"
                    data-testid="form-description"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="form-submit"
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Wysyłam...
                    </>
                  ) : (
                    <>
                      Wyślij zgłoszenie
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p className="text-xs text-white/40 text-center">
                  Wysyłając akceptujesz, że odezwiemy się na podany adres.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
